import React, { useMemo } from 'react';
import Plot from 'react-plotly.js';
import { useData } from '../../context/DataContext';
import { computeDotPlotData } from '../../utils/dotPlotData';

/**
 * Compute separator positions and group header annotations for a dot plot.
 * Works with any number of columns by detecting group boundaries from COLUMN_GROUPS.
 */
function getDotPlotDecorations(nCols, colLabels, nGenes, visibleIndices) {
  // Determine group boundaries from the visible indices
  // Groups: ImmGen (0-3), A20 (4-5), EmBO Genotypes (6-8), EmBO Transformed (9-11)
  const groupBoundaries = [
    { label: "ImmGen (Yoshida '19)", origIndices: [0, 1, 2, 3] },
    { label: 'A20', origIndices: [4, 5] },
    { label: 'EmBO Genotypes', origIndices: [6, 7, 8] },
    { label: 'EmBO Transformed', origIndices: [9, 10, 11] },
  ];

  const shapes = [];
  const annotations = [];

  // Map original indices to visible positions
  const origToVisible = {};
  visibleIndices.forEach((orig, vis) => { origToVisible[orig] = vis; });

  let prevGroupEnd = -1;
  for (const group of groupBoundaries) {
    const visibleInGroup = group.origIndices
      .filter(i => origToVisible[i] !== undefined)
      .map(i => origToVisible[i]);

    if (visibleInGroup.length === 0) continue;

    const groupStart = Math.min(...visibleInGroup);
    const groupEnd = Math.max(...visibleInGroup);

    // Add separator between this group and previous
    if (prevGroupEnd >= 0 && groupStart > prevGroupEnd) {
      const sepX = (prevGroupEnd + groupStart) / 2;
      shapes.push({
        type: 'line',
        x0: sepX, x1: sepX,
        y0: -0.5, y1: nGenes - 0.5,
        line: { color: '#333', width: 1.5 },
        xref: 'x', yref: 'y',
      });
    }

    // Group header annotation
    const centerX = (groupStart + groupEnd) / 2;
    annotations.push({
      x: centerX, y: -1.5,
      text: group.label,
      showarrow: false,
      font: { size: 11, color: '#777' },
      xref: 'x', yref: 'y',
    });

    prevGroupEnd = groupEnd;
  }

  return { separatorShapes: shapes, groupAnnotations: annotations };
}

export default function DotPlot({ panelName, colorMode = 'relative' }) {
  const { data, getFilteredData } = useData();

  const { filteredExpression, filteredColLabels, visibleIndices } = useMemo(
    () => getFilteredData(),
    [getFilteredData]
  );

  const plotData = useMemo(() => {
    if (!data || !panelName || !data.panels[panelName]) return null;

    const panel = data.panels[panelName];
    const genes = [];
    const subcatMap = {};

    for (const [subcat, geneList] of Object.entries(panel)) {
      for (const gene of geneList) {
        genes.push(gene);
        subcatMap[gene] = subcat;
      }
    }

    return computeDotPlotData({
      genes,
      expression: filteredExpression,
      colLabels: filteredColLabels,
      colorMode,
      subcatMap,
    });
  }, [data, panelName, colorMode, filteredExpression, filteredColLabels]);

  if (!plotData) return <div className="plot-placeholder">Select a panel to view</div>;

  const nGenes = plotData.genes.length;
  const nCols = plotData.nCols;
  const plotHeight = Math.max(400, nGenes * 28 + 150);

  // Subcategory color shapes (left side)
  const subcatShapes = [];
  const uniqueSubcats = [...new Set(plotData.subcats)];
  for (let i = 0; i < plotData.subcats.length; i++) {
    const color = data.subcatColors[plotData.subcats[i]] || '#888';
    subcatShapes.push({
      type: 'rect',
      x0: -1.8, x1: -1.2,
      y0: i - 0.4, y1: i + 0.4,
      fillcolor: color,
      line: { width: 0 },
      xref: 'x', yref: 'y',
    });
  }

  // Separator lines and group headers
  const { separatorShapes, groupAnnotations } = getDotPlotDecorations(
    nCols, filteredColLabels, nGenes, visibleIndices
  );

  // Missing gene rows
  const missingShapes = plotData.missingRows.map(i => ({
    type: 'rect',
    x0: -0.5, x1: nCols - 0.5,
    y0: i - 0.4, y1: i + 0.4,
    fillcolor: '#f0f0f0',
    line: { color: '#ccc', width: 0.5, dash: 'dot' },
    xref: 'x', yref: 'y',
  }));

  const missingAnnotations = plotData.missingRows.map(i => ({
    x: nCols / 2, y: i,
    text: '<i>no murine ortholog</i>',
    showarrow: false,
    font: { size: 10, color: '#999' },
    xref: 'x', yref: 'y',
  }));

  // Subcategory legend
  const legendAnnotations = uniqueSubcats.map((sc, i) => ({
    x: 0.02 + (i % 4) * 0.25,
    y: -0.08 - Math.floor(i / 4) * 0.04,
    text: `<b style="color:${data.subcatColors[sc] || '#888'}">■</b> ${sc.replace(/_/g, ' ')}`,
    showarrow: false,
    font: { size: 10, color: '#555' },
    xref: 'paper', yref: 'paper',
    xanchor: 'left',
  }));

  // Size legend traces
  const sizeLegendTraces = plotData.sizeRefVals.map(v => {
    const markerSize = Math.max(4, (v / plotData.maxLog) * 30);
    return {
      type: 'scatter',
      mode: 'markers',
      x: [null], y: [null],
      marker: { size: markerSize, color: '#ccc', line: { width: 0.5, color: '#999' } },
      name: `${v.toFixed(0)}`,
      showlegend: true,
      legendgroup: 'size',
      hoverinfo: 'skip',
    };
  });

  const traces = [
    {
      type: 'scatter',
      mode: 'markers',
      x: plotData.x,
      y: plotData.y,
      marker: {
        size: plotData.sizes,
        color: plotData.colors,
        colorscale: plotData.colorscale,
        cmin: plotData.cmin,
        cmax: plotData.cmax,
        colorbar: {
          title: { text: plotData.colorbarTitle, font: { size: 11, color: '#333' } },
          tickfont: { size: 10, color: '#555' },
          len: 0.4,
          thickness: 12,
          x: 1.06,
          outlinewidth: 0,
        },
        line: { width: 0.5, color: '#999' },
      },
      text: plotData.hoverTexts,
      hoverinfo: 'text',
      showlegend: false,
    },
    ...sizeLegendTraces,
  ];

  const layout = {
    width: Math.max(700, 400 + nCols * 50),
    height: plotHeight,
    paper_bgcolor: '#ffffff',
    plot_bgcolor: '#ffffff',
    margin: { l: 120, r: 80, t: 60, b: 30 + Math.ceil(uniqueSubcats.length / 4) * 22 },
    xaxis: {
      tickvals: [...Array(nCols).keys()],
      ticktext: filteredColLabels,
      side: 'top',
      tickfont: { size: 10, color: '#333' },
      range: [-2, nCols],
      gridcolor: '#eee',
      zeroline: false,
      fixedrange: true,
      showline: true,
      linecolor: '#ccc',
      linewidth: 1,
    },
    yaxis: {
      tickvals: [...Array(nGenes).keys()],
      ticktext: plotData.genes.map(g => `<i>${g}</i>`),
      autorange: 'reversed',
      tickfont: { size: 10, color: '#333', family: 'Inter' },
      gridcolor: '#eee',
      zeroline: false,
      fixedrange: true,
      showline: true,
      linecolor: '#ccc',
      linewidth: 1,
    },
    shapes: [...subcatShapes, ...separatorShapes, ...missingShapes],
    annotations: [...missingAnnotations, ...groupAnnotations, ...legendAnnotations],
    hovermode: 'closest',
    legend: {
      title: { text: 'log₂(expr+1)', font: { size: 10, color: '#555' } },
      x: 1.02, y: 0.15,
      font: { size: 9, color: '#555' },
      bgcolor: 'rgba(255,255,255,0)',
      borderwidth: 0,
      tracegroupgap: 0,
      itemsizing: 'constant',
    },
  };

  return (
    <div className="dot-plot-container">
      <Plot
        data={traces}
        layout={layout}
        config={{ displayModeBar: false, responsive: true }}
      />
    </div>
  );
}
